import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, distinctUntilChanged, map, noop } from 'rxjs';

import { Models, ModelsCreateParams, ModelsExtract, ModelsExtractParams } from '../models/model.model';
import { DistinctSubject, recursiveQuery } from '../models/utility.model';
import { AuthUsecase } from '../usecases/auth.usecase';
import { ModelGateway } from '../usecases/model.gateway';
import { ModelUsecase } from '../usecases/model.usecase';

@Injectable()
export class ModelInteractor extends ModelUsecase {
  get models$(): Observable<Models> {
    return this._models;
  }
  get extractModels$(): Observable<ModelsExtract[]> {
    return this._extractModels;
  }

  private readonly _models = new DistinctSubject<Models>(new Models());
  private readonly _extractModels = new DistinctSubject<ModelsExtract[]>([]);

  constructor(
    private _authUsecase: AuthUsecase,
    private _modelGateway: ModelGateway,
  ) {
    super();

    this._authUsecase.authState$
      .pipe(
        map(({ status }) => status === 'signedIn'),
        distinctUntilChanged(),
      )
      .subscribe(signedIn => (signedIn ? noop : this.onSignOut()));
  }

  listModels(): Observable<never> {
    const result = new AsyncSubject<never>();
    recursiveQuery(params => this._modelGateway.listModels(params), {}).subscribe({
      next: models => this._models.next(new Models(models)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  listExtractModels(params: ModelsExtractParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._extractModels.next([]);
    this._modelGateway.listExtractModels(params).subscribe({
      next: items => this._extractModels.next(items.items),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createModels(params: ModelsCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._modelGateway.createModels(params).subscribe({
      next: ({ items }) => {
        const models = items.reduce((acc, cur) => acc.set(cur), this._models.value);
        this._models.next(models);
      },
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  deleteModel(modelId: string): Observable<never> {
    const result = new AsyncSubject<never>();
    this._modelGateway.deleteModel(modelId).subscribe({
      next: () => this._models.next(this._models.value.delete(modelId)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  private onSignOut(): void {
    this._models.next(new Models());
  }
}
